import { IconButton, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ScoutIcon from "components/ScoutIcon";
import { useFrontChatPresent } from "hooks/useFrontChatPresent";
import { useCallback, useState } from "react";

import { MinimizedScoutBar } from "./MinimizedScoutBar";
import { PopoverScout, type PopoverScoutDock } from "./PopoverScout";
import { useScoutPopoverSession } from "./useScoutPopoverSession";

// Both the popover and the minimized bar dock to the same screen corner so
// the transition between modes feels like a state change in one element.
const DOCK: PopoverScoutDock = { corner: "bottom-left", offsetX: 40, offsetY: 40 };

// Front's launcher is ~56px wide at left:20 of the viewport. When Front is
// loaded the Scout launcher shifts right by Front's full footprint + a 20px
// gap; when Front is absent Scout sits in Front's normal spot.
const LAUNCHER_LEFT_WITHOUT_FRONT = 20;
const LAUNCHER_LEFT_WITH_FRONT = 96;

type ViewMode = "closed" | "open" | "minimized";

/**
 * Fixed-position circular launcher mirroring Front's bottom-right chat
 * bubble. Lives at bottom-left of the viewport so the two launchers visually
 * pair up. Opens a Popover Scout conversation; supports a minimized state
 * (docked bottom-left) for keeping a conversation available while the user
 * works in the main view.
 */
export function ScoutSidebarFooterTrigger() {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>("closed");
  const session = useScoutPopoverSession();
  const frontPresent = useFrontChatPresent();
  const launcherLeft = frontPresent ? LAUNCHER_LEFT_WITH_FRONT : LAUNCHER_LEFT_WITHOUT_FRONT;

  const handleOpen = useCallback(() => setViewMode("open"), []);
  const handleClose = useCallback(() => setViewMode("closed"), []);
  const handleMinimize = useCallback(() => setViewMode("minimized"), []);

  return (
    <>
      {/*
        Hidden while the popover or minimized bar is showing — the launcher
        is the "closed" entry point and would otherwise stack behind those
        surfaces.
      */}
      {viewMode === "closed" ? (
        <Tooltip
          title="Ask Scout"
          placement="right"
          enterDelay={100}
          disableInteractive
          slotProps={{ tooltip: { sx: { pointerEvents: "none" } } }}
        >
          <IconButton
            onClick={handleOpen}
            aria-label="Ask Scout"
            sx={{
              position: "fixed",
              // Sits at the bottom-left where Front normally lives, and
              // shifts right when Front is present so both stay visible.
              bottom: 20,
              left: launcherLeft,
              transition: "left 200ms ease",
              width: 56,
              height: 56,
              borderRadius: "50%",
              bgcolor: theme.palette.encampBlue.main,
              color: "white",
              boxShadow: 3,
              // Above Front's iframe (which Encamp pins to 1201 so MUI modals
              // at 1300 still cover it). Sits between drawer and modal.
              zIndex: 1250,
              "&:hover": {
                bgcolor: "#4A88D0",
              },
            }}
          >
            <ScoutIcon sx={{ fontSize: 28, color: "white" }} />
          </IconButton>
        </Tooltip>
      ) : null}
      <PopoverScout
        anchorEl={null}
        open={viewMode === "open"}
        onClose={handleClose}
        onMinimize={handleMinimize}
        session={session}
        dock={DOCK}
      />
      {viewMode === "minimized" ? (
        <MinimizedScoutBar
          conversationId={session.conversationId}
          onExpand={handleOpen}
          onClose={handleClose}
        />
      ) : null}
    </>
  );
}
